import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { navigation as extensionNavigation } from "@afriannotate/extensions";
import { activeHostMode } from "../../utils/hostMode";
import { NavLink, useHistory } from "react-router-dom";
import { useBreadcrumbControls } from "../../services/breadrumbs";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useTranslation } from "../../i18n/LocaleProvider";
import { SyncModal } from "../../pages/UserProfile/SyncModal";
import { FeedbackModal } from "../FeedbackModal/FeedbackModal";
import { TestReportLauncher } from "../TestReport/TestReportLauncher";
import { getTestConfig, loadTestConfig, openTestReport, subscribeTestConfig } from "../TestReport/testReportStore";
import { Modal } from "../Modal/Modal";
import { VerifyReceiptForm } from "../VerifyReceipt/VerifyReceiptForm";
import { syncModalOpenAtom, syncStateRevAtom } from "../../pages/UserProfile/syncModalAtom";
import { useQueryClient } from "@tanstack/react-query";
import { hasTokens, clearTokens, clearCachedIdentity } from "../../utils/jwt-storage";
import { clearAllOfflineStores } from "../../utils/offline-outbox";
import { isCloudReachable } from "../../hooks/useOnlineStatus";
import { spaNavigate } from "../../utils/navigation";
import { refreshAuthHeaders } from "../../providers/ApiProvider";

// Sharp outline icons matching IconLayers2's stroke style (stroke-width=2, round
// linecap/linejoin, 24x24 viewBox). Used in the sidebar so Home / Projects
// visually match the other section icons.
const IconHomeOutline = (props) => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 11.5L12 4l9 7.5" />
    <path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" />
  </svg>
);
const IconProjectsOutline = (props) => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
  </svg>
);
import { StaticContent } from "../../app/StaticContent/StaticContent";
import {
  IconBook,
  IconFolder,
  IconHome,
  IconHotkeys,
  IconPeople,
  IconPin,
  IconStar,
  IconTerminal,
  IconDoor,
  IconGithub,
  IconSlack,
  IconLayers2,
  IconBell,
  IconCheckCircle,
} from "@humansignal/icons";
import { Button, ThemeToggle, Userpic, themeAtom } from "@humansignal/ui";
import { OfflinePill } from "../OfflinePill/OfflinePill";
import { useConfig } from "../../providers/ConfigProvider";
import { useContextComponent, useFixedLocation } from "../../providers/RoutesProvider";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { useAPI } from "@humansignal/core";
import { useBranding } from "../../providers/BrandingProvider";
import { cn } from "../../utils/bem";
import { absoluteURL, isDefined } from "../../utils/helpers";
import { Breadcrumbs } from "../Breadcrumbs/Breadcrumbs";
import { Dropdown } from "@humansignal/ui";
import { Hamburger } from "../Hamburger/Hamburger";
import { ThemeSegment } from "./ThemeSegment";
import { IdleLogout } from "../IdleLogout/IdleLogout";
import { Menu } from "../Menu/Menu";
import { VersionNotifier, VersionProvider } from "../VersionNotifier/VersionNotifier";
import { SidebarOrgHeader } from "../OrgSwitcher/SidebarOrgHeader";
import "./Menubar.prefix.css";
import "./MenuContent.prefix.css";
import "./MenuSidebar.prefix.css";
import { FF_HOMEPAGE } from "../../utils/feature-flags";
import { isFF } from "../../utils/feature-flags";
import { ff } from "@humansignal/core";
import {
  openHotkeyHelp,
  isHotkeyHelpDismissed,
} from "@humansignal/app-common/pages/AccountSettings/sections/Hotkeys/Help";

export const MenubarContext = createContext();

const LeftContextMenu = ({ className }) => (
  <StaticContent id="context-menu-left" className={className}>
    {(template) => <Breadcrumbs fromTemplate={template} />}
  </StaticContent>
);

/** On a project's pages: the project's name, linking to its dashboard.
 *  The full breadcrumb chain is left out there (the section pills say
 *  where you are), but without the name nothing on the page said which
 *  project it was. */
const ProjectTitle = ({ className }) => {
  const crumbs = useBreadcrumbControls();
  const project = crumbs.find((c) => /^\/projects\/\d+\/?$/.test(c.path ?? ""));
  if (!project?.title) return <div className={className} />;
  return (
    <div className={className}>
      <NavLink to={project.path} className="lsf-menu-header__project-title" title={project.title} data-external>
        {project.title}
      </NavLink>
    </div>
  );
};

const RightContextMenu = ({ className, ...props }) => {
  const { ContextComponent, contextProps } = useContextComponent();

  return ContextComponent ? (
    <div className={className}>
      <ContextComponent {...props} {...(contextProps ?? {})} />
    </div>
  ) : (
    <StaticContent id="context-menu-right" className={className} />
  );
};

const HotkeyFAB = () => {
  const { t } = useTranslation();
  // Floating keyboard-shortcuts button with an "install banner"-
  // style × close affordance: clicking × hides the FAB for 24h,
  // clicking the FAB itself opens the help modal. Re-evaluates the
  // dismiss stamp on mount + window focus so the FAB reappears on
  // its own once the TTL elapses (no reload required).
  const [hidden, setHidden] = useState(() => isHotkeyHelpDismissed());
  useEffect(() => {
    const update = () => setHidden(isHotkeyHelpDismissed());
    window.addEventListener("afri-hotkey-help-dismissed", update);
    window.addEventListener("focus", update);
    return () => {
      window.removeEventListener("afri-hotkey-help-dismissed", update);
      window.removeEventListener("focus", update);
    };
  }, []);
  if (hidden) return null;

  const onDismiss = (e) => {
    // The × is rendered inside the FAB so clicks bubble — stop
    // propagation so the help modal doesn't ALSO open.
    e.stopPropagation();
    e.preventDefault();
    try {
      window.localStorage.setItem("afri_hotkey_help_dismissed_at", String(Date.now()));
      window.dispatchEvent(new Event("afri-hotkey-help-dismissed"));
    } catch (_) {
      /* private mode */
    }
  };

  // The wrap is the fixed-position anchor (see Menubar.prefix.css) so the
  // dismiss X anchors to the FAB and hides with it on mobile — as an
  // in-flow relative wrap, the X's right:-6px widened the body by 6px on
  // every page and stayed visible after the FAB's ≤768 display:none.
  return (
    <div className="lsf-hotkeys-fab-wrap">
      <button
        className="lsf-hotkeys-fab"
        onClick={() =>
          openHotkeyHelp([
            "annotation",
            "data_manager",
            "regions",
            "tools",
            "audio",
            "video",
            "timeseries",
            "image_gallery",
          ])
        }
        aria-label={t("components.menubar.menubar.keyboard_shortcuts")}
        title={t("components.menubar.menubar.keyboard_shortcuts")}
      >
        <IconHotkeys />
      </button>
      <button
        type="button"
        className="lsf-hotkeys-fab-close"
        onClick={onDismiss}
        aria-label={t(
          "components.menubar.menubar.hide_keyboardshortcuts_button_for_24_hou",
          undefined,
          "Hide keyboard-shortcuts button for 24 hours",
        )}
        title={t("components.menubar.menubar.hide_for_24_hours", undefined, "Hide for 24 hours")}
        style={{
          position: "absolute",
          top: -6,
          right: -6,
          width: 20,
          height: 20,
          minHeight: 20,
          minWidth: 20,
          maxHeight: 20,
          padding: 0,
          borderRadius: "50%",
          border: "1px solid var(--color-neutral-border, rgba(0,0,0,0.18))",
          background: "var(--color-neutral-background, #fff)",
          color: "var(--color-neutral-content-subtler, #6b6b6b)",
          fontSize: "var(--font-size-caption)",
          lineHeight: 1,
          cursor: "pointer",
          boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        ×
      </button>
    </div>
  );
};

// A host mode without an annotation editor replaces the keyboard-shortcuts
// button with a quiet floating link to its own handbook.
const DocsFAB = ({ href, label }) => (
  <a href={href} className="lsf-docs-fab" title={label} aria-label={label}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 5.5A1.5 1.5 0 0 1 5.5 4H18a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H6a2 2 0 0 1-2-2V5.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M4 17.5A1.5 1.5 0 0 1 5.5 16H19" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M8 8.5h7M8 11.5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
    <span className="lsf-docs-fab__label">{label}</span>
  </a>
);

const fmtRelative = (iso) => {
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
};

const isDesktopApp = () => {
  if (typeof window === "undefined") return false;
  const { hostname, port } = window.location;
  return (hostname === "127.0.0.1" || hostname === "localhost") && port === "17080";
};

/** Human name for a route, derived from its PATH — not from document.title,
 *  which project sub-pages set to "<ProjectName> | <Page>" (so the page
 *  name isn't reliably positioned) and which some pages (e.g. Analytics)
 *  never update at all. Uses the same ``nav.*`` labels as the sidebar, so
 *  the destination reads identically to where the user clicked. */
const labelForPath = (pathname, t) => {
  if (!pathname) return null;
  const p = pathname.replace(/[?#].*$/, "").replace(/\/+$/, "") || "/";

  // Project-scoped routes: /projects/:id[/sub]
  const proj = p.match(/^\/projects\/\d+(?:\/([a-z-]+))?/i);
  if (proj) {
    const sub = (proj[1] || "").toLowerCase();
    const map = {
      "": ["nav.dashboard", "Dashboard"],
      data: ["nav.data_manager", "Data Manager"],
      members: ["nav.members", "Members"],
      analytics: ["nav.analytics", "Analytics"],
      settings: ["nav.settings", "Settings"],
    };
    const entry = map[sub] || map[""];
    return t(entry[0], undefined, entry[1]);
  }

  // Top-level routes.
  const seg = p.split("/")[1] || "";
  const top = {
    "": ["nav.home", "Home"],
    projects: ["nav.projects", "Projects"],
    discover: ["pages.discover.title", "Discover"],
    organization: ["nav.organization", "Organization"],
    platform: ["nav.platform", "Platform"],
    users: ["nav.account", "Account"],
  };
  if (top[seg]) return t(top[seg][0], undefined, top[seg][1]);
  // Fallback: title-case the first segment.
  return seg ? seg.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : null;
};

/**
 * Desktop Back button that names its destination — "‹ Data Manager".
 *
 * The Tauri WebView has no OS chrome, so from a focused view (a labeling
 * instance) the user was stranded. This drives the SPA's own router
 * history, so "back" goes exactly where they came from, and it names that
 * destination from the back-target's PATH (see ``labelForPath``).
 *
 * Deliberately Back-only: the app already renders a breadcrumb (see
 * ``LeftContextMenu``) for orientation and level-jumping — the native
 * wayfinding idiom — so a browser-style "forward" arrow is omitted.
 * When there is nothing to go back to, the button hides entirely rather
 * than sitting disabled. Desktop-only; the web build has the browser's
 * own back button.
 */
const DesktopBackButton = () => {
  const { t } = useTranslation();
  const history = useHistory();
  // Stack of visited paths, oldest → current, seeded with the entry the
  // app opened on. We track only paths — the label is computed from the
  // back-target's path, so there's no title-timing to get wrong.
  const stackRef = useRef([typeof window !== "undefined" ? window.location.pathname : "/"]);
  const [, bump] = useState(0);

  useEffect(() => {
    const unlisten = history.listen((loc, action) => {
      const s = stackRef.current;
      if (action === "PUSH") s.push(loc.pathname);
      else if (action === "POP") {
        if (s.length > 1) s.pop();
      } else if (action === "REPLACE") s[s.length - 1] = loc.pathname;
      bump((n) => n + 1);
    });
    return unlisten;
  }, [history]);

  if (!isDesktopApp()) return null;
  const s = stackRef.current;
  if (s.length <= 1) return null; // nothing to go back to → hide entirely

  // Home is the app's root/anchor — hide Back while sitting on it, so the
  // top of the app never offers "‹ Platform" (or wherever you came from).
  // The stack stays intact, so Home → Project still shows "‹ Home".
  const currentPath = (s[s.length - 1] || "/").replace(/[?#].*$/, "").replace(/\/+$/, "") || "/";
  if (currentPath === "/") return null;

  const destination = labelForPath(s[s.length - 2], t);
  const label = destination
    ? t("components.menubar.menubar.back_to", { page: destination }, `Back to ${destination}`)
    : t("components.menubar.menubar.back", undefined, "Back");

  return (
    <button
      type="button"
      onClick={() => {
        // Use the BROWSER's history, not react-router's. On desktop
        // ``spaNavigate`` navigates via ``window.location.href`` (a hard
        // load), which react-router's in-memory history never records —
        // so ``history.goBack()`` can't reach those entries and does
        // nothing. ``window.history.back()`` pops both hard and soft
        // navigations, matching how the app actually moves between pages.
        if (typeof window !== "undefined" && window.history.length > 1) window.history.back();
        else history.goBack();
      }}
      aria-label={label}
      title={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        height: 30,
        padding: destination ? "0 13px 0 9px" : "0 9px",
        marginRight: 6,
        border: "1px solid var(--color-neutral-border, #e2e2e2)",
        borderRadius: 999,
        background: "transparent",
        cursor: "pointer",
        maxWidth: 240,
        color: "var(--color-neutral-content, #333)",
        fontSize: "var(--font-size-body-small, 13px)",
        fontWeight: "var(--font-weight-medium)",
        transition: "background 120ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--color-neutral-emphasis, #ececec)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {/* Bold curved back arrow (matches the requested style). */}
      <svg
        viewBox="0 0 24 24"
        width="17"
        height="17"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        style={{ flexShrink: 0 }}
      >
        <path d="M20 19a8 8 0 0 0-8-8H5" />
        <polyline points="9 15 4 11 9 7" />
      </svg>
      {destination && (
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{destination}</span>
      )}
    </button>
  );
};

/**
 * Menubar pill that surfaces three states the desktop user cares
 * about, in priority order:
 *
 *   1. Offline    — the cloud isn't reachable right now. Shown so
 *                   the user knows their work will be queued, not
 *                   silently dropped.
 *   2. Pending    — cloud reachable AND there are local edits
 *                   waiting to push. The classic "X to push"
 *                   prompt; click to review and sync.
 *   3. Restored   — cloud just came back online and there's
 *                   pending work. Same as Pending but flashes a
 *                   one-shot "Connection restored" toast so the
 *                   user notices without watching the pill.
 *
 * Cadence: pings /api/afri/auth/cloud_reachable/ + pending_count
 * every 30s, plus on window focus AND on browser ``online`` /
 * ``offline`` events so we react instantly when the OS sees a
 * link state change instead of waiting for the next tick.
 */
const PendingPushBadge = () => {
  const { t } = useTranslation();
  const desktop = isDesktopApp();
  const setSyncOpen = useSetAtom(syncModalOpenAtom);
  // Subscribed to the global "sync state changed" rev — anything
  // that mutates queue size (the modal's Apply, manual per-project
  // push) bumps the rev so we re-poll immediately instead of
  // waiting for the next 30s tick.
  const syncRev = useAtomValue(syncStateRevAtom);
  const [pending, setPending] = useState(0);
  const [online, setOnline] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);
  const [restoredToast, setRestoredToast] = useState(null);
  const prevOnlineRef = useRef(true);
  const prevSyncingRef = useRef(false);
  const prevPendingRef = useRef(0);
  const justSyncedTimerRef = useRef(null);

  useEffect(() => {
    if (!desktop) return;
    let alive = true;
    const tick = async () => {
      try {
        const [cr, pc] = await Promise.all([
          fetch("/api/afri/auth/cloud_reachable/", { cache: "no-store" }).then((r) => r.json()),
          fetch("/api/afri/sync/pending_count/", { cache: "no-store" }).then((r) => r.json()),
        ]);
        if (!alive) return;
        const isOnline = !!(cr && cr.reachable);
        const pendingCount = (pc && pc.count) || 0;
        const isSyncing = !!(pc && pc.is_syncing);
        // Offline → online transition with pending work. Kick off
        // an autopush in the background so the queue drains without
        // the user having to open the modal — and surface a toast
        // so they notice even if they aren't watching the pill.
        if (isOnline && !prevOnlineRef.current && pendingCount > 0) {
          setRestoredToast({ count: pendingCount, ts: Date.now() });
          fetch("/api/afri/sync/push/", {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              "X-CSRFToken": (document.cookie.match(/csrftoken=([^;]+)/) || [])[1] || "",
            },
            body: JSON.stringify({}),
          }).catch(() => {
            /* swallow — next 5s tick will re-evaluate */
          });
        }
        // Syncing-just-finished transition: the most recent SyncRun
        // had finished_at IS NULL, now it's set. Show "Synced ✓" for
        // ~2s if we landed in a clean state, mirroring Google Docs's
        // "Saved to Drive" confirmation. Skip if pending is still >0
        // (failure / retry) so we don't claim success when there's
        // unflushed work.
        if (prevSyncingRef.current && !isSyncing && pendingCount === 0 && prevPendingRef.current > 0 && isOnline) {
          if (justSyncedTimerRef.current) clearTimeout(justSyncedTimerRef.current);
          setJustSynced(true);
          justSyncedTimerRef.current = setTimeout(() => {
            if (alive) setJustSynced(false);
          }, 2200);
        }
        prevSyncingRef.current = isSyncing;
        prevPendingRef.current = pendingCount;
        prevOnlineRef.current = isOnline;
        setOnline(isOnline);
        setPending(pendingCount);
        setSyncing(isSyncing);
      } catch (_) {
        if (alive) {
          prevOnlineRef.current = false;
          setOnline(false);
          // Deliberately DON'T clear ``syncing`` here: a failed tick
          // usually means the local Django is busy servicing a heavy
          // pull — exactly when the pill must keep saying "Syncing…",
          // not flip to "Offline". The next successful tick corrects
          // the state either way.
        }
      }
    };
    tick();
    // 5s cadence so the pill snaps to "1 to push" within a beat of
    // the user clicking Submit on an annotation, and clears just as
    // fast after Apply succeeds. Endpoint is purely-local SQLite
    // counts — cheap enough to hit aggressively. The 30s ceiling we
    // had before lagged the pill behind the user's actions enough
    // that they assumed the autopush had failed.
    const id = setInterval(() => {
      if (!document.hidden) tick();
    }, 5 * 1000);
    const onFocus = () => tick();
    const onOnline = () => tick();
    const onOffline = () => {
      prevOnlineRef.current = false;
      setOnline(false);
    };
    // ``visibilitychange`` covers the case where the desktop window
    // wasn't focused but came forward — useful for the sub-window
    // labeling pop-out where ``focus`` doesn't fire on the parent.
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVis);
    };
    // ``syncRev`` in the dep array forces the hook to re-run on
    // every bump, which kicks off a fresh ``tick()`` and clears
    // a stale "to push" pill the moment Apply succeeds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktop, syncRev]);

  // Auto-dismiss the restored toast after 8s if the user doesn't
  // act on it. The pill stays — that's the persistent surface.
  useEffect(() => {
    if (!restoredToast) return undefined;
    const t = setTimeout(() => setRestoredToast(null), 8000);
    return () => clearTimeout(t);
  }, [restoredToast]);

  if (!desktop) return null;

  // Open the GitHub-style review modal in place — the user
  // shouldn't have to navigate to the Cloud sync tab just to push.
  // The modal is mounted once at the top of Menubar so this works
  // from any page.
  const goSync = () => {
    setRestoredToast(null);
    setSyncOpen(true);
  };

  // Pill display priority (Google Docs idiom):
  //   1. Syncing — pulse "Syncing…" while a push/pull is in flight.
  //   2. Just synced — flash "Synced ✓" briefly after a clean
  //      finish so the user has confirmation before the pill
  //      disappears.
  //   3. Offline — cloud unreachable; show queued count if any.
  //   4. Pending — online but the queue isn't draining (push
  //      failed, retry pending, or autopush hasn't fired yet).
  //   5. Idle + clean — render nothing.
  // ``syncing`` intentionally beats the reachability probe: an
  // in-flight pull/push IS talking to the cloud, so "Offline" while
  // rows are streaming in was a lie (the probe just timed out against
  // the busy local server). Reported during the 2026-07-18 offline
  // test round.
  let pill = null;
  if (syncing) {
    pill = (
      <button
        type="button"
        onClick={goSync}
        title={t("components.menubar.menubar.syncing_your_changes_to_the_cloud")}
        style={pillStyle({ tone: "syncing" })}
      >
        <span aria-hidden="true" style={spinnerStyle} />
        Syncing…
      </button>
    );
  } else if (online && justSynced) {
    pill = (
      <button
        type="button"
        onClick={goSync}
        title={t("components.menubar.menubar.local_changes_are_now_on_the_cloud")}
        style={pillStyle({ tone: "synced" })}
      >
        <span aria-hidden="true" style={{ ...dotStyle("#1f7a5a"), background: "#1f7a5a" }} />
        Synced ✓
      </button>
    );
  } else if (!online) {
    pill = (
      <button
        type="button"
        onClick={goSync}
        title={t("components.menubar.menubar.cloud_isnt_reachable_right_now_your_work")}
        style={pillStyle({ tone: "offline" })}
      >
        <span aria-hidden="true" style={dotStyle("#888")} />
        Offline{pending > 0 ? `, ${pending} queued` : ""}
      </button>
    );
  } else if (pending > 0) {
    pill = (
      <button
        type="button"
        onClick={goSync}
        title={`${pending} ${pending === 1 ? "annotation is" : "annotations are"} ready to push to the cloud. Click to review.`}
        style={pillStyle({ tone: "pending" })}
      >
        <span aria-hidden="true" style={dotStyle("#c0353a")} />
        {pending} to push
      </button>
    );
  }

  return (
    <>
      {pill}
      {restoredToast && (
        <div
          role="status"
          aria-live="polite"
          onClick={goSync}
          style={{
            position: "fixed",
            right: 24,
            bottom: 24,
            zIndex: 1200,
            background: "var(--color-neutral-background, #fff)",
            color: "var(--color-neutral-content, #1c1c1c)",
            border: "1px solid var(--color-primary-border-subtle, #c0353a)",
            borderLeft: "4px solid var(--color-primary-surface, #c0353a)",
            borderRadius: 8,
            padding: "10px 14px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            fontSize: "var(--font-size-body-small)",
            cursor: "pointer",
            maxWidth: 320,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 2 }}>{t("components.menubar.menubar.connection_restored")}</div>
          <div style={{ color: "var(--color-neutral-content-subtler, #6b6b6b)" }}>
            {restoredToast.count} local change{restoredToast.count === 1 ? "" : "s"} ready to sync. Click to review.
          </div>
        </div>
      )}
    </>
  );
};

const PILL_TONE_STYLES = {
  syncing: {
    bg: "rgba(78,134,219,0.14)",
    fg: "#2b5fae",
    border: "rgba(78,134,219,0.35)",
  },
  synced: {
    bg: "rgba(31,122,90,0.14)",
    fg: "#1f7a5a",
    border: "rgba(31,122,90,0.35)",
  },
  offline: {
    bg: "rgba(120,120,120,0.12)",
    fg: "#555",
    border: "rgba(120,120,120,0.3)",
  },
  pending: {
    bg: "rgba(214,158,46,0.14)",
    fg: "#8a5a00",
    border: "rgba(214,158,46,0.35)",
  },
};

const pillStyle = ({ tone }) => {
  const t = PILL_TONE_STYLES[tone] || PILL_TONE_STYLES.pending;
  return {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px 4px 8px",
    marginRight: 8,
    background: t.bg,
    color: t.fg,
    border: `1px solid ${t.border}`,
    borderRadius: 999,
    fontSize: "var(--font-size-caption)",
    fontWeight: 500,
    cursor: "pointer",
  };
};

const dotStyle = (color) => ({
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: color,
  display: "inline-block",
});

const spinnerStyle = {
  width: 10,
  height: 10,
  borderRadius: "50%",
  border: "1.5px solid rgba(78,134,219,0.35)",
  borderTopColor: "#2b5fae",
  animation: "afri-spin 0.9s linear infinite",
  display: "inline-block",
};

// Inject the keyframes once at module load — avoids needing a CSS
// file just for a 4-line @keyframes rule.
if (typeof document !== "undefined" && !document.getElementById("afri-spin-keyframes")) {
  const s = document.createElement("style");
  s.id = "afri-spin-keyframes";
  s.textContent = "@keyframes afri-spin { to { transform: rotate(360deg); } }";
  document.head.appendChild(s);
}

const NotificationsBell = () => {
  const api = useAPI();
  const { t } = useTranslation();
  // Desktop: notifications are written by CLOUD-side actions (reviews,
  // rate changes, payouts) into the cloud DB — the sync layer doesn't
  // mirror them, so the local list is permanently empty and a bell
  // that always says "no notifications yet" reads as broken. Hide it;
  // the same events reach the user by email and on the web app.
  // (``isDesktopApp()`` is constant for the session — stable hooks.)
  if (isDesktopApp()) return null;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  // A host mode can scope the bell to its own notification kinds.
  const notifApp = activeHostMode?.notificationsScope || "label";

  const loadCount = useCallback(async () => {
    const res = await api.callApi("notificationsUnreadCount", { params: { app: notifApp }, suppressError: true });
    if (res && !res.error) setUnread(res.unread ?? 0);
  }, [api, notifApp]);

  const loadList = useCallback(async () => {
    setLoading(true);
    const res = await api.callApi("notifications", { params: { page_size: 20, app: notifApp }, suppressError: true });
    setLoading(false);
    if (res && !res.error) setItems(Array.isArray(res) ? res : (res.results ?? []));
  }, [api, notifApp]);

  // Poll the unread count periodically and on focus/visibility change.
  // SSE was removed because the dev server's threaded runserver saturated
  // under many concurrent long-lived connections; polling is cheap and good
  // enough for a notification badge.
  useEffect(() => {
    let cancelled = false;
    loadCount();
    const pollTimer = setInterval(() => {
      if (!cancelled && !document.hidden) loadCount();
    }, 15000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") loadCount();
    };
    // Org switch on Capacitor: SidebarOrgHeader fires this event
    // instead of doing a full page reload, so the bell's local
    // state (unread count + list) — which would otherwise still
    // reflect the previous org's notifications — needs an explicit
    // reset + refetch.
    const onOrgSwitched = () => {
      setUnread(0);
      setItems([]);
      loadCount();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", loadCount);
    window.addEventListener("afri:org-switched", onOrgSwitched);
    return () => {
      cancelled = true;
      clearInterval(pollTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", loadCount);
      window.removeEventListener("afri:org-switched", onOrgSwitched);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      loadList();
      loadCount();
    }
  };

  const handleClick = async (n) => {
    await api.callApi("notificationMarkRead", { params: { pk: n.id } });
    setOpen(false);
    if (n.link_url) window.location.href = n.link_url;
    loadCount();
  };

  const markAllRead = async () => {
    await api.callApi("notificationsMarkAllRead", { params: { app: notifApp }, suppressError: true });
    setUnread(0);
    loadList();
  };

  return (
    <div className="lsf-menu-header__bell-wrap">
      <button
        type="button"
        className="lsf-menu-header__bell"
        onClick={toggle}
        aria-label={t("nav.notifications")}
        title={t("nav.notifications")}
      >
        <IconBell />
        {unread > 0 && <span className="lsf-menu-header__bell-badge">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <>
          <div className="lsf-menu-header__bell-backdrop" onClick={() => setOpen(false)} />
          <div className="lsf-menu-header__bell-dropdown">
            <div className="lsf-menu-header__bell-header">
              <span>{t("nav.notifications")}</span>
              {items.some((n) => !n.read_at) && (
                <button type="button" className="lsf-menu-header__bell-mark-all" onClick={markAllRead}>
                  Mark all read
                </button>
              )}
            </div>
            <div className="lsf-menu-header__bell-list">
              {loading ? (
                <div className="lsf-menu-header__bell-empty">Loading…</div>
              ) : items.length === 0 ? (
                <div className="lsf-menu-header__bell-empty">
                  {t("components.menubar.menubar.no_notifications_yet")}
                </div>
              ) : (
                items.map((n) => {
                  // Org pill: pulled from notification metadata (set by
                  // server-side ``notify(..., organization=org)`` since
                  // 2026-04-30). For users who belong to multiple orgs,
                  // this is the at-a-glance signal of WHICH workspace
                  // a notification is about. Notifications themselves
                  // are still user-scoped (intentional — see
                  // notification model docstring) so this pill is the
                  // only contextual cue between orgs.
                  const orgTitle = n.metadata?.organization_title || "";
                  return (
                    <button
                      key={n.id}
                      type="button"
                      className={`lsf-menu-header__bell-item${n.read_at ? "" : " lsf-menu-header__bell-item_unread"}`}
                      onClick={() => handleClick(n)}
                    >
                      <div className="lsf-menu-header__bell-item-title">{n.title}</div>
                      {n.body && <div className="lsf-menu-header__bell-item-body">{n.body}</div>}
                      <div className="lsf-menu-header__bell-item-meta">
                        {orgTitle && (
                          <span className="lsf-menu-header__bell-item-org" title={`Organization: ${orgTitle}`}>
                            {orgTitle}
                          </span>
                        )}
                        <span className="lsf-menu-header__bell-item-time">{fmtRelative(n.created_at)}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const Menubar = ({
  enabled,
  sidebar = true,
  defaultOpened,
  defaultPinned,
  children,
  onSidebarToggle,
  onSidebarPin,
}) => {
  const history = useHistory();
  const menuDropdownRef = useRef();
  const useMenuRef = useRef();
  const queryClient = useQueryClient();
  const { user, isLoading } = useAuth();
  // Signed-in gate for the app controls AND the sidebar. Normally
  // ``user?.id``; on Capacitor a live access token counts too, so the
  // boot window before whoami resolves (or a failed first whoami) does
  // not collapse the sidebar to Home-only / flash "Sign in".
  const signedIn = useMemo(() => {
    if (user?.id) return true;
    try {
      if (typeof window === "undefined") return false;
      if (typeof window.Capacitor === "undefined") return false;
      // Accept a live ACCESS *or* REFRESH token — the SAME rule
      // HomePage's ``sessionAlive`` uses. A 24h access token can be
      // expired during the boot/refresh window while the 30d refresh is
      // still valid (and about to mint a fresh access); checking access
      // ALONE wrongly flashed a "Sign in" button over a home that WAS
      // rendering — the two identity gates disagreed (seen on the app:
      // "Welcome back, there" + Sign in, then the real identity a beat
      // later). Keeping them consistent removes that flash.
      const now = Math.floor(Date.now() / 1000);
      const liveTok = (key) => {
        const tok = window.localStorage.getItem(key);
        if (!tok) return false;
        const parts = tok.split(".");
        if (parts.length < 2) return false;
        try {
          const claims = JSON.parse(
            atob(
              ((b) => {
                while (b.length % 4) b += "=";
                return b;
              })(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
            ),
          );
          return typeof claims.exp === "number" && claims.exp > now;
        } catch {
          return false;
        }
      };
      return liveTok("afri:jwt:access") || liveTok("afri:jwt:refresh");
    } catch (_) {
      return false;
    }
  }, [user?.id]);
  // Last-known avatar cache — bridges the whoami round-trip on boot /
  // refocus so the Userpic never flashes blank/initials for a user
  // whose photo we already showed. Stored per email; cleared when the
  // live user has no avatar (photo removed).
  const avatarUser = useMemo(() => {
    try {
      const key = "afri:last-avatar";
      if (user?.id) {
        if (user.avatar) {
          window.localStorage?.setItem(
            key,
            JSON.stringify({
              id: user.id,
              email: user.email,
              avatar: user.avatar,
              first_name: user.first_name,
              last_name: user.last_name,
            }),
          );
        } else {
          window.localStorage?.removeItem(key);
        }
        return user;
      }
      const raw = window.localStorage?.getItem(key);
      if (!raw) return user;
      const cached = JSON.parse(raw);
      // Signed storage URLs expire (24h GCS). A stale cached avatar
      // makes the <img> error → broken picture. Check the signature
      // window; if it has lapsed, don't offer the cached URL.
      try {
        const q = new URL(cached.avatar).searchParams;
        const d = q.get("X-Goog-Date") || q.get("X-Amz-Date");
        const secs = Number(q.get("X-Goog-Expires") || q.get("X-Amz-Expires"));
        const m = d && d.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
        if (m && Number.isFinite(secs)) {
          const signedAt = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
          if (Date.now() > signedAt + secs * 1000 - 60_000) return user;
        }
      } catch (_) {
        /* not a signed URL — fine to use */
      }
      return cached;
    } catch (_) {
      return user;
    }
    // Stable deps: a whoami refetch yields a new ``user`` object with the
    // same identity — re-deriving the avatar on every refetch re-rendered
    // the Userpic (visible "refresh" each time the window regained focus).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.email, user?.avatar, user?.first_name, user?.last_name]);
  const branding = useBranding();
  const location = useFixedLocation();
  const { t } = useTranslation();

  const config = useConfig();
  // On project-scoped pages the ProjectNav pills already say where you
  // are and the project title is in the page header — the breadcrumb
  // chain duplicated both and was the widest element in a crowded bar
  // (owner-requested removal).
  const isProjectScoped = /^\/projects\/\d+/.test(location?.pathname ?? "");
  const [sidebarOpened, setSidebarOpened] = useState(defaultOpened ?? false);
  const [sidebarPinned, setSidebarPinned] = useState(defaultPinned ?? false);
  // Receipt verification modal — sidebar entry (utility cluster, by
  // the pin) so it's discoverable without leaving the shell.
  const [verifyOpen, setVerifyOpen] = useState(false);
  // Track narrow viewports so we can force the sidebar to behave as a
  // floating dropdown (no persistent pinned state). The user's saved
  // preference is left untouched — once the viewport widens again the
  // sidebar can be re-pinned without re-clicking. Default to `false`
  // for SSR / first paint to match the CSS.
  const [isNarrow, setIsNarrow] = useState(typeof window !== "undefined" ? window.innerWidth <= 900 : false);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const effectivePinned = sidebarPinned && !isNarrow;
  const safeLS = (op, key, val) => {
    try {
      if (op === "get") return localStorage.getItem(key);
      if (op === "set") localStorage.setItem(key, val);
    } catch (_) {
      // Safari private mode or storage disabled — silently ignore.
    }
    return null;
  };
  const canManageOrg = !!(user && (user.is_staff || ["OW", "AD", "MA"].includes(user.active_organization_role)));
  // Navigation contributed by an installed extension package. ``when``
  // gates visibility the same way the platform's own entries are gated;
  // ``after`` places an entry under one of them instead of at the end.
  const extensionItems = (after) =>
    (extensionNavigation ?? [])
      .filter((item) => item.after === after)
      .filter((item) => {
        if (item.when === "staff") return !!user?.is_staff;
        if (item.when === "org_manager") return !!canManageOrg;
        if (item.when === "signed_in") return !!signedIn;
        return true;
      })
      .map((item) => (
        <Menu.Item
          key={item.to}
          label={item.label}
          to={item.to}
          icon={item.icon ?? <IconLayers2 />}
          data-external
          exact={!!item.exact}
        />
      ));
  const profileMenuHref = activeHostMode?.profileHref || (user?.id ? `/users/${user.id}` : "/");
  // Logo swaps to a lighter variant in dark mode so the tree doesn't go
  // muddy on the near-black header (themeAtom resolves Auto → system).
  const theme = useAtomValue(themeAtom);
  // AfriAnnotate (main app) brand lockup + compact mark — the default brand
  // when the operator hasn't set a custom logo via Platform Settings.
  const annotateLockupSrc =
    String(theme).toLowerCase() === "dark"
      ? "/static/brand/afriannotate/lockup-dark.png?v=6"
      : "/static/brand/afriannotate/lockup.png?v=6";
  const annotateMarkSrc =
    String(theme).toLowerCase() === "dark"
      ? "/static/brand/afriannotate/mark-dark.png?v=1"
      : "/static/brand/afriannotate/mark.png?v=1";
  // Sign-in target (logged-out cluster). A safe ``next`` that
  // never points back at an auth page (else sign-in would loop to login).
  //
  // Web/desktop: point at the Django ``/user/login/`` URL — Django
  // redirects that to ``/auth/login`` server-side (auth consolidation),
  // but the ``next=`` querystring survives the redirect so the user
  // lands back where they were after signing in.
  //
  // Capacitor: point straight at the React ``/auth/login`` — the
  // bundled WebView has no Django to run the redirect, and any
  // ``window.location.assign`` on the local origin would trigger a
  // WebView reload that loses the bundle's live state. The onClick
  // handler below routes through ``spaNavigate`` on Capacitor to keep
  // the current index.html loaded.
  const onCapacitorHost = typeof window !== "undefined" && typeof window.Capacitor !== "undefined";
  const signInHref = (() => {
    const p = typeof window !== "undefined" ? window.location.pathname : "/";
    const safe = /^\/(user\/(login|signup|logout)|auth|logout)\b/.test(p) ? "/" : p;
    if (onCapacitorHost) {
      return `/auth/login?next=${encodeURIComponent(safe)}`;
    }
    if (activeHostMode?.signInHref) {
      return `${activeHostMode.signInHref}?next=${encodeURIComponent(safe)}`;
    }
    return `/user/login/?next=${encodeURIComponent(safe)}`;
  })();
  // Brand shown in the chrome-less header: the host mode's own lockup, else
  // the operator's logo, else the platform lockup.
  const hostBrand = activeHostMode?.brand ?? null;
  const headerLockupSrc = hostBrand
    ? String(theme).toLowerCase() === "dark" && hostBrand.lockupDark
      ? hostBrand.lockupDark
      : hostBrand.lockup
    : branding.logo_url || annotateLockupSrc;
  const headerLockupAlt = hostBrand ? hostBrand.alt || "" : `${branding.name} logo`;
  // Self-signups land orgless until invited. They can use the
  // sidebar's Home link + their profile, but org-scoped destinations
  // (Projects index, Organization page) would 403 — hide those
  // links so they don't lead to a dead end. Staff bypass this gate
  // so they can navigate to staff-scoped pages even without a
  // workspace.
  const hasOrg = !!(user && (user.active_organization || user.is_staff));
  const [PageContext, setPageContext] = useState({
    Component: null,
    props: {},
  });

  const menubarClass = cn("menu-header");
  const menubarContext = menubarClass.elem("context");
  const sidebarClass = cn("sidebar");
  const contentClass = cn("content-wrapper");
  const contextItem = menubarClass.elem("context-item");
  const showNewsletterDot = false; // disabled: AfriAnnotate doesn't run a newsletter opt-in flow
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // In-app testing programme: the menu entry appears only while it is open
  // (Platform → Testing). Config is fetched once and shared with the launcher.
  const testApi = useAPI();
  const [testProgramme, setTestProgramme] = useState(getTestConfig());
  useEffect(() => {
    if (!user?.id) return;
    loadTestConfig(testApi).then(setTestProgramme);
    return subscribeTestConfig(setTestProgramme);
  }, [testApi, user?.id]);

  const sidebarPin = useCallback(
    (e) => {
      e.preventDefault();

      const newState = !sidebarPinned;

      setSidebarPinned(newState);
      onSidebarPin?.(newState);
    },
    [sidebarPinned],
  );

  const sidebarToggle = useCallback(
    (visible) => {
      const newState = visible;

      setSidebarOpened(newState);
      onSidebarToggle?.(newState);
    },
    [sidebarOpened],
  );

  const providerValue = useMemo(
    () => ({
      PageContext,

      setContext(ctx) {
        setTimeout(() => {
          setPageContext({
            ...PageContext,
            Component: ctx,
          });
        });
      },

      setProps(props) {
        setTimeout(() => {
          setPageContext({
            ...PageContext,
            props,
          });
        });
      },

      contextIsSet(ctx) {
        return PageContext.Component === ctx;
      },
    }),
    [PageContext],
  );

  useEffect(() => {
    if (!effectivePinned) {
      menuDropdownRef?.current?.close();
    }
    useMenuRef?.current?.close();
  }, [location, effectivePinned]);

  return (
    // ``chromeless``: no fixed header rendered, so don't reserve the
    // header strip either.
    <div className={`${contentClass}${enabled ? "" : " lsf-content-wrapper_chromeless"}`}>
      {/* Keyboard/screen-reader escape hatch past the menubar — visually
          hidden until focused (see .lsf-skip-link). */}
      <a className="lsf-skip-link" href="#main-content">
        Skip to content
      </a>
      {/* Per-project session_idle_timeout_minutes auto-logout. No-op
          when no project is loaded or the project's timeout is 0. */}
      <IdleLogout />
      {enabled && (
        <div className={menubarClass}>
          {sidebar ? (
            <Dropdown.Trigger dropdown={menuDropdownRef} closeOnClickOutside={!effectivePinned}>
              <div className={`${menubarClass.elem("trigger")} main-menu-trigger`}>
                {branding.logo_url ? (
                  <img
                    className={`${menubarClass.elem("brand-logo")}`}
                    src={branding.logo_url}
                    alt={`${branding.name} logo`}
                  />
                ) : (
                  <img
                    className={`${menubarClass.elem("brand-logo")}`}
                    src={annotateLockupSrc}
                    alt={`${branding.name} logo`}
                    title={branding.name}
                    /* Height + display come from Menubar.prefix.css so the
                       max-width: 900px rule that hides the full lockup on
                       phones (leaving only the compact __brand-icon
                       stand-in) actually fires. An inline
                       ``display: block`` here was overriding the CSS
                       ``display: none`` on mobile — both logos rendered
                       side-by-side instead of the intended lockup-desktop
                       / icon-only-mobile pair. */
                  />
                )}
                {/* Compact brand stand-in — CSS hides it above the narrow
                    breakpoint and hides the full logo/text below it. When
                    an ``icon_url`` is configured we render that square
                    logomark; otherwise we fall back to a letter avatar
                    derived from ``branding.name``. Keeps the trigger
                    square-ish on phones so the bell / theme toggle /
                    user dropdown stay reachable. */}
                {branding.icon_url ? (
                  <img
                    className={menubarClass.elem("brand-icon").toClassName()}
                    src={branding.icon_url}
                    alt={`${branding.name} icon`}
                    title={branding.name}
                  />
                ) : (
                  <img
                    className={menubarClass.elem("brand-icon").toClassName()}
                    src={annotateMarkSrc}
                    alt={`${branding.name} icon`}
                    title={branding.name}
                  />
                )}
                <Hamburger opened={sidebarOpened} />
              </div>
            </Dropdown.Trigger>
          ) : (
            // Chrome-less header: a plain brand lockup linking home — no
            // menu dropdown, hamburger or page-context breadcrumb.
            <a
              href={hostBrand?.homeHref || "/"}
              style={{
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                padding: "0 16px",
                height: "100%",
              }}
            >
              <img
                src={headerLockupSrc}
                alt={headerLockupAlt}
                title={hostBrand ? headerLockupAlt : branding.name}
                style={{ height: 36, width: "auto", display: "block" }}
              />
            </a>
          )}

          {sidebar ? (
            <div className={menubarContext} style={{ display: "flex", alignItems: "center" }}>
              <DesktopBackButton />
              {isProjectScoped ? (
                <ProjectTitle className={contextItem.mod({ left: true }).toClassName()} />
              ) : (
                <LeftContextMenu className={contextItem.mod({ left: true }).toClassName()} />
              )}
              <RightContextMenu className={contextItem.mod({ right: true }).toClassName()} />
            </div>
          ) : (
            // Spacer so the user cluster (bell + avatar) stays right-aligned.
            <div style={{ flex: 1 }} />
          )}

          {/* Divider separates the project nav pills from the user
              cluster (bell + avatar dropdown) so those two personal
              controls read as a grouped unit, not as siblings of the
              navigation. */}
          <span className={`${menubarClass.elem("divider")}`} aria-hidden="true" />

          <div className={`${menubarClass.elem("app-controls")}`}>
            {/* Signed-in gate. Normally driven by ``user?.id`` from
              ``useAuth()``. Capacitor caveat: the whoami query that
              feeds ``user`` runs against the cloud and can be slow /
              flaky on cellular right after login — during that
              window the JWT is already in localStorage and the app
              IS authenticated at the network layer, but ``user`` is
              still null, and we'd wrongly flash a "Sign in" button
              at a user who's actually signed in. On Capacitor we
              treat a live access-token as a second-source auth
              signal: same rule the boot-script pivot uses to decide
              whether to bounce to ``/auth/login``. Web / desktop
              stay on the strict ``user?.id`` check — those hosts
              share-cookie their auth and don't have the same
              startup race. */}
            {signedIn ? (
              <>
                {/* Web/mobile only — on desktop the sync pill (PendingPushBadge)
              already surfaces the offline state, so showing both was
              redundant. */}
                {!isDesktopApp() && <OfflinePill />}
                <PendingPushBadge />
                <NotificationsBell />
                <Dropdown.Trigger
                  ref={useMenuRef}
                  align="right"
                  content={
                    <Menu className={cn("menu-header").elem("user-menu").toClassName()}>
                      {/* Identity header doubles as the Profile link. Keeping
                    it as a single clickable row means we don't waste
                    another menu slot on "Profile" — the identity row
                    itself is the shortest path to your profile.

                    Fallback: if the tanstack whoami hasn't populated
                    yet but the user IS authenticated (JWT in
                    localStorage — that's what unlocked this dropdown
                    branch in the first place), synthesize a stub
                    user with just the email from the JWT payload.
                    Better to show "signed-in as X" than a blank row
                    that hides the profile link entirely. */}
                      {(() => {
                        if (user?.id) return user;
                        try {
                          const t = window.localStorage?.getItem("afri:jwt:access");
                          if (!t) return null;
                          const parts = t.split(".");
                          if (parts.length < 2) return null;
                          const claims = JSON.parse(
                            atob(
                              ((b) => {
                                while (b.length % 4) b += "=";
                                return b;
                              })(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
                            ),
                          );
                          if (!claims?.user_id) return null;
                          return {
                            id: claims.user_id,
                            email: claims.email || "Signed in",
                            first_name: "",
                            last_name: "",
                          };
                        } catch (_) {
                          return null;
                        }
                      })() && (
                        <li className={cn("menu-header").elem("user-menu-id-wrap").toClassName()}>
                          <a
                            href={profileMenuHref}
                            className={cn("menu-header").elem("user-menu-id").toClassName()}
                            aria-label={t("profile.open_profile")}
                            onClick={(e) => {
                              // Capacitor: AsyncPage's capture-phase listener
                              // already does this, but on iOS WKWebView the
                              // anchor's href can race the JS handler. Drive
                              // the SPA navigation explicitly so the WebView
                              // never tries to fetch the URL from the local
                              // file bundle (which 404s and on iOS may hand the
                              // URL to Safari). Web/desktop fall through to the
                              // normal AsyncPage flow.
                              if (typeof window.Capacitor !== "undefined") {
                                e.preventDefault();
                                spaNavigate(profileMenuHref);
                              }
                            }}
                          >
                            <Userpic
                              user={user ?? { email: "", first_name: "", last_name: "" }}
                              style={{ width: 40, height: 40, fontSize: "var(--font-size-title-sm)" }}
                            />
                            <div className={cn("menu-header").elem("user-menu-id-text").toClassName()}>
                              <span className={cn("menu-header").elem("user-menu-id-name").toClassName()}>
                                {[user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
                                  user?.email ||
                                  (() => {
                                    // Fallback name = email from JWT payload.
                                    try {
                                      const t = window.localStorage?.getItem("afri:jwt:access");
                                      const parts = t?.split(".") ?? [];
                                      if (parts.length < 2) return "Signed in";
                                      const claims = JSON.parse(
                                        atob(
                                          ((b) => {
                                            while (b.length % 4) b += "=";
                                            return b;
                                          })(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
                                        ),
                                      );
                                      return claims?.email || "Signed in";
                                    } catch (_) {
                                      return "Signed in";
                                    }
                                  })()}
                              </span>
                              {user?.email && (
                                <span className={cn("menu-header").elem("user-menu-id-email").toClassName()}>
                                  {user.email}
                                </span>
                              )}
                            </div>
                            <span className={cn("menu-header").elem("user-menu-id-arrow").toClassName()} aria-hidden>
                              ›
                            </span>
                          </a>
                        </li>
                      )}
                      {/* "My templates" moved from this dropdown to a tab on
                    the user profile page (owner-requested). The old
                    /project-templates URL redirects there. */}
                      {showNewsletterDot && user?.id && (
                        <>
                          <Menu.Divider />
                          <Menu.Item className={cn("newsletter-menu-item").toClassName()} href={profileMenuHref}>
                            <span>{t("components.menubar.menubar.new_notification_settings_take_a_look")}</span>
                            <span className={cn("newsletter-menu-badge").toClassName()} />
                          </Menu.Item>
                        </>
                      )}
                      {/* Send feedback — saved to the platform's feedback queue
                    (Platform → Feedback) for periodic review. Plain <li> +
                    button, NOT Menu.Item: this hand-rolled user dropdown
                    isn't wrapped in <Menu>, so Menu.Item renders nothing
                    here (the disabled newsletter block never caught that). */}
                      {/* Action rows — same shape as the identity row above
                    (icon slot + label, hover tint, no boxed button) so
                    the menu reads as one list. */}
                      {user?.id && (
                        <li className={cn("menu-header").elem("action-row").toClassName()}>
                          <a
                            href={`/users/${user.id}/tokens`}
                            className={cn("menu-header").elem("action-btn").toClassName()}
                            onClick={(e) => {
                              useMenuRef?.current?.close();
                              if (typeof window.Capacitor !== "undefined") {
                                e.preventDefault();
                                spaNavigate(`/users/${user.id}/tokens`);
                              }
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden
                            >
                              <circle cx="8" cy="12" r="4" />
                              <path d="M12 12h9M18 12v3M15 12v2" />
                            </svg>
                            <span>API tokens</span>
                          </a>
                        </li>
                      )}
                      <li className={cn("menu-header").elem("action-row").toClassName()}>
                        <button
                          type="button"
                          className={cn("menu-header").elem("action-btn").toClassName()}
                          onClick={() => {
                            // Close the user dropdown first — it stacks above the
                            // modal overlay, so leaving it open buries the feedback
                            // form under the menu (the phone-testing repro).
                            useMenuRef?.current?.close();
                            setFeedbackOpen(true);
                          }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                          <span>Send feedback</span>
                        </button>
                      </li>
                      {testProgramme?.enabled && (
                        <li className={cn("menu-header").elem("action-row").toClassName()}>
                          <button
                            type="button"
                            className={cn("menu-header").elem("action-btn").toClassName()}
                            onClick={() => {
                              useMenuRef?.current?.close();
                              openTestReport();
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden
                            >
                              <path d="M4 4h16v11H8l-4 4z" />
                              <path d="M12 7v4" />
                              <path d="M12 13.5h.01" />
                            </svg>
                            <span>Report a test step</span>
                          </button>
                        </li>
                      )}
                      {testProgramme?.enabled && (
                        <li className={cn("menu-header").elem("action-row").toClassName()}>
                          <button
                            type="button"
                            className={cn("menu-header").elem("action-btn").toClassName()}
                            onClick={() => {
                              useMenuRef?.current?.close();
                              history.push("/testing-checklist");
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden
                            >
                              <path d="M9 11l3 3 8-8" />
                              <path d="M20 12v7a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h11" />
                            </svg>
                            <span>My testing checklist</span>
                          </button>
                        </li>
                      )}
                      {/* Offline storage inspector — mobile only (the page
                    itself no-ops elsewhere, but don't advertise it on
                    web/desktop where downloads live in other UIs). */}
                      {typeof window !== "undefined" && window.Capacitor && (
                        <li className={cn("menu-header").elem("action-row").toClassName()}>
                          <button
                            type="button"
                            className={cn("menu-header").elem("action-btn").toClassName()}
                            onClick={() => {
                              useMenuRef?.current?.close();
                              spaNavigate("/offline-storage");
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden
                            >
                              <ellipse cx="12" cy="5" rx="9" ry="3" />
                              <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                              <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                            </svg>
                            <span>Offline storage</span>
                          </button>
                        </li>
                      )}
                      {/* Theme picker — segmented Auto / Light / Dark control.
                    Sleeker than the single pill toggle that looked
                    orphaned in a list of text items; each option is one
                    tap with visible active state. */}
                      {ff.isActive(ff.FF_THEME_TOGGLE) && (
                        <>
                          <Menu.Divider />
                          <li className={cn("menu-header").elem("theme-menu-row").toClassName()}>
                            <span className={cn("menu-header").elem("theme-menu-label").toClassName()}>Theme</span>
                            <ThemeSegment />
                          </li>
                        </>
                      )}
                      <li className={cn("menu-header").elem("logout-row").toClassName()}>
                        {/* Capacitor renders Sign Out as a <button>, not an <a>:
                      iOS WKWebView's nav delegate inspects an anchor's
                      href BEFORE the React onClick fires, and any URL
                      it considers "external" is handed off to Safari
                      with no chance for ``e.preventDefault()`` to stop
                      it. A button has no href, so there is nothing to
                      hand off — the click only flows through our JS.
                      Web/desktop keep the <a href="/logout"> so Django's
                      session-cookie logout still runs on a hard reload. */}
                        {typeof window.Capacitor !== "undefined" ? (
                          <button
                            type="button"
                            className={cn("menu-header").elem("logout-btn").toClassName()}
                            aria-label={t("common.sign_out")}
                            onClick={async () => {
                              const cloudUp = isCloudReachable();
                              // Online sign-out: full wipe. The user is
                              // intentionally ending the session and the
                              // cloud is reachable, so we clear the JWT
                              // (cloud-side tokens still survive but the
                              // device no longer has them), drop the
                              // queryClient, wipe IDB (whoami / projects /
                              // tasks / annotations / outbox / cached URL
                              // bytes — anything that could leak to the
                              // next account), and reload. Pre-boot finds
                              // no JWT → routes to /auth/login.
                              //
                              // Offline sign-out: partial. The same user
                              // may want to sign back in offline (their
                              // cached projects + queued annotations need
                              // to be intact). Keep the JWT + IDB; only
                              // drop the queryClient (in-memory React
                              // state) so the next render starts clean.
                              // ``Login.tsx``'s offline branch will route
                              // to / when the entered email matches the
                              // cached whoami email — a different user
                              // can't bypass auth this way.
                              refreshAuthHeaders();
                              queryClient.clear();
                              if (cloudUp) {
                                clearTokens();
                                // Wipe the optimistic identity cache too — else
                                // the next account inherits this user's avatar
                                // AND privileged nav during its post-login boot
                                // window (a signed-out staff account left
                                // afri:last-user behind and a following non-staff
                                // sign-in momentarily showed Platform).
                                clearCachedIdentity();
                                // Kick the IDB wipe off WITHOUT awaiting yet — it
                                // and the server-side logout are independent, and
                                // the wipe can be slow when a project has been
                                // downloaded for offline use (the media store
                                // holds up to 500 MB). Awaiting them in series
                                // made sign-out feel hung.
                                const wipe = clearAllOfflineStores().catch(() => undefined);
                                try {
                                  window.sessionStorage?.removeItem("afri:active-org-override");
                                } catch (_) {}
                                // Also kill the Django session cookie on the
                                // cloud. Without this, the mobile app's next
                                // launch still finds the WebView on
                                // ``afriannotate.waraka.org/…`` with a live session
                                // cookie and Django auto-authenticates —
                                // ``clearTokens()`` only cleared the JWT in
                                // this origin's localStorage, which does
                                // NOTHING to the HttpOnly ``sessionid`` cookie
                                // Django set on the cloud origin. Explicitly
                                // fetch /logout from the current origin so
                                // Django receives the cookie in-flight and
                                // invalidates the session server-side. Mirror
                                // the web/desktop path's ``redirect: manual``
                                // + ``cache: no-store`` so we don't chase a
                                // 302 into a re-authenticated login page.
                                try {
                                  // ``absoluteURL`` resolves to
                                  // ``https://afriannotate.waraka.org/logout`` (via
                                  // baked ``APP_SETTINGS.hostname``). On the
                                  // Capacitor local origin (``http://localhost``)
                                  // this is a cross-origin fetch — must be
                                  // ``credentials: 'include'`` to send the
                                  // Django ``sessionid`` cookie set on the cloud
                                  // origin. ``same-origin`` would drop the
                                  // cookie and the logout would be a no-op.
                                  // ``redirect: 'follow'`` lets Django's 302
                                  // land on the login page — we don't render
                                  // that response, we just need the request
                                  // to hit the server so the session is
                                  // invalidated.
                                  // ``manual``, not ``follow``: the server only
                                  // needs to RECEIVE the request to invalidate the
                                  // session. Following the 302 downloaded the whole
                                  // redirect target (login page / SPA shell) and we
                                  // then threw it away — pure latency on the one
                                  // action a user wants to be instant.
                                  //
                                  // Capped: the local credentials are already gone,
                                  // so a slow or dead network must not hold the user
                                  // on a page they've asked to leave. If the request
                                  // doesn't land, the session cookie is invalidated
                                  // by its own expiry instead.
                                  const ctrl = new AbortController();
                                  const t = setTimeout(() => ctrl.abort(), 3000);
                                  await fetch(absoluteURL("/logout"), {
                                    method: "GET",
                                    credentials: "include",
                                    cache: "no-store",
                                    redirect: "manual",
                                    headers: { Accept: "text/html" },
                                    signal: ctrl.signal,
                                  }).finally(() => clearTimeout(t));
                                } catch (_) {
                                  /* offline / network dead — best effort */
                                }
                                // Data separation still matters: the next account
                                // must not see this one's cached tasks. Await the
                                // wipe here, where it has been running in parallel
                                // with the request above rather than after it.
                                try {
                                  await wipe;
                                } catch (_) {}
                              }
                              // Both paths: route to login. Pre-boot will
                              // hit /auth/login if there's no JWT (online
                              // path) or skip the redirect if JWT is
                              // intact (offline path). Either way, the
                              // user lands on the login form.
                              window.location.replace(cloudUp ? "/" : "/auth/login");
                            }}
                          >
                            <IconDoor />
                            <span>{t("common.sign_out")}</span>
                          </button>
                        ) : (
                          <a
                            href={absoluteURL("/logout")}
                            data-external
                            className={cn("menu-header").elem("logout-btn").toClassName()}
                            aria-label={t("common.sign_out")}
                            onClick={async (e) => {
                              // Three signals to kill, in strict order, so we
                              // never end up on a page that still thinks the
                              // user is signed in:
                              //   1. JWT in localStorage (every session login
                              //      mints one — cookie users have a JWT too).
                              //   2. React-query cache (any cached whoami).
                              //   3. Django session cookie — fetch /logout
                              //      WITH cache:no-store and AWAIT it before
                              //      navigating. The previous "let the <a>
                              //      fire" path kicked the browser into a
                              //      redirect chain (/logout → / → login)
                              //      that the SW could short-circuit with a
                              //      cached / version, explaining the
                              //      "needs two clicks" bug.
                              e.preventDefault();
                              if (hasTokens()) {
                                clearTokens();
                                refreshAuthHeaders();
                              }
                              // Always wipe the identity cache (afri:last-user /
                              // afri:last-avatar) — cookie-auth users may have no
                              // JWT but still leave a cached identity the next
                              // account would inherit on boot.
                              clearCachedIdentity();
                              queryClient.clear();
                              try {
                                await fetch(absoluteURL("/logout"), {
                                  method: "GET",
                                  credentials: "same-origin",
                                  cache: "no-store",
                                  headers: { Accept: "text/html" },
                                  redirect: "manual",
                                });
                              } catch {
                                /* offline — keep going anyway */
                              }
                              // Public host modes land on their signed-out home rather than the login page.
                              window.location.replace(absoluteURL(activeHostMode?.signedOutHref || "/user/login/"));
                            }}
                          >
                            <IconDoor />
                            <span>{t("common.sign_out")}</span>
                          </a>
                        )}
                      </li>
                    </Menu>
                  }
                >
                  <div
                    title={user?.email}
                    className={menubarClass.elem("user").toClassName()}
                    role="button"
                    tabIndex={0}
                    aria-label="Account menu"
                    aria-haspopup="menu"
                    onKeyDown={(e) => {
                      // Dropdown.Trigger only clones a click handler onto this
                      // div — synthesize the click so keyboard users can open
                      // their own account menu.
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.currentTarget.click();
                      }
                    }}
                  >
                    {/* size 34 to match the notification bell button beside it.
                  ``avatarUser`` = live user, or the last-known avatar
                  URL from localStorage while whoami is still in flight,
                  so a refocus/reload paints the photo on the first
                  frame instead of blank → initials → photo. */}
                    <Userpic user={avatarUser} size={34} isInProgress={isLoading && !avatarUser} />
                    {showNewsletterDot && <div className={menubarClass.elem("userpic-badge").toClassName()} />}
                  </div>
                </Dropdown.Trigger>
              </>
            ) : (
              // Signed-out: just theme + Sign in — no bell or user dropdown
              // (which would poll member-only APIs / 401).
              <>
                <ThemeToggle />
                <a
                  href={signInHref}
                  onClick={(e) => {
                    e.preventDefault();
                    // Capacitor: SPA-nav to the local React Login route.
                    // A ``location.assign`` here would reload the WebView
                    // on the local origin, drop the live SPA state, and
                    // pass control back to the boot script's pivot — a
                    // costly round-trip that also re-fires the version /
                    // feature-flag probes for no gain. ``spaNavigate``
                    // just swaps the router path in place.
                    //
                    // Web/desktop: keep the full navigation to Django's
                    // ``/user/login/`` — Django redirects to the React
                    // ``/auth/login`` route server-side and the browser
                    // does a normal page load. The SW self-heal covers
                    // the stale-worker case.
                    if (onCapacitorHost) {
                      spaNavigate(signInHref, true);
                    } else {
                      window.location.assign(signInHref);
                    }
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "7px 16px",
                    borderRadius: 8,
                    background: "#1f5b3f",
                    color: "#fff",
                    textDecoration: "none",
                    fontWeight: 600,
                    fontSize: "var(--font-size-body-small)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Sign in
                </a>
              </>
            )}
          </div>
        </div>
      )}

      <VersionProvider>
        <div id="main-content" role="main" className={contentClass.elem("body").toClassName()}>
          {enabled && sidebar && (
            <Dropdown
              ref={menuDropdownRef}
              onToggle={sidebarToggle}
              onVisibilityChanged={() => window.dispatchEvent(new Event("resize"))}
              visible={sidebarOpened}
              className={[sidebarClass, sidebarClass.mod({ floating: !effectivePinned })].join(" ")}
              /* Width lives in MenuSidebar.prefix.css (240px desktop,
                 min(85vw, 320px) ≤768px). An inline width here beat the
                 stylesheet and kept the phone drawer at desktop width. */
            >
              {/* Org card at the top of the sidebar — active org
                  name + switcher dropdown, mirrors the LS/HumanSignal
                  cloud pattern. The "Organization" main-menu item
                  below is now a plain navigation entry (same
                  affordance as Platform) instead of a collapsible
                  group header. */}
              {/* Hide the org switcher for orgless users — they have
                  nothing to switch to, so it just renders "No
                  organization" as visual noise. Staff users are the
                  exception: they can jump into ANY org via the
                  switcher even without membership. Also hide for
                  users on the crowdsource contributor path (they
                  reach a project via ProjectContributor, not org
                  membership) — the switcher is orthogonal to their
                  workflow. See CROWDSOURCE_ARCHITECTURE.md. */}
              {hasOrg && <SidebarOrgHeader canManageOrg={canManageOrg} />}

              <Menu>
                {/* Home was upstream-gated by FF_HOMEPAGE
                    (``fflag_all_feat_dia_1777_ls_homepage_short``). The
                    Capacitor bundle ships ``feature_flags: {}`` so the
                    flag was always off there — Home went missing on
                    mobile. The page itself is wired into the route
                    table unconditionally (see ``pages/index.js``), so
                    show the sidebar link unconditionally too. */}
                <Menu.Item label={t("nav.home")} to="/" icon={<IconHomeOutline />} data-external exact />
                {/* One "Projects" entry for everyone. It hosts two tabs —
                    "My projects" (org projects) and "Discover" (public
                    crowdsource feed) — so there's no separate Discover
                    sidebar link. Orgless users land on the Discover tab by
                    default (they have no org projects). See slice 6 in
                    native/CROWDSOURCE_ARCHITECTURE.md. */}
                {signedIn && (
                  <Menu.Item
                    label={t("nav.projects")}
                    to="/projects"
                    icon={<IconProjectsOutline />}
                    data-external
                    exact
                  />
                )}
                {extensionItems("projects")}
                {/* Organization hides completely for users who can't
                    manage it (same affordance as Platform). Previously
                    we routed non-managers to /projects instead — same
                    end state but a disabled-looking entry confused
                    testers. */}
                {canManageOrg && (
                  <Menu.Item label={t("nav.organization")} to="/organization" icon={<IconPeople />} data-external />
                )}
                {user?.is_staff && (
                  <Menu.Item label={t("nav.platform")} to="/platform" icon={<IconLayers2 />} data-external />
                )}
                {extensionItems(undefined)}

                <Menu.Spacer />

                {/* Upgrade-available notifier still mounts (so the
                    update toast can fire) but no current-version
                    label in the sidebar — it adds noise without
                    helping the user. */}
                <VersionNotifier showNewVersion />

                {branding.api_docs_url && (
                  <Menu.Item
                    label={t("nav.api")}
                    href={branding.api_docs_url}
                    icon={<IconTerminal />}
                    target="_blank"
                  />
                )}
                {branding.docs_url && (
                  <Menu.Item label={t("nav.docs")} href={branding.docs_url} icon={<IconBook />} target="_blank" />
                )}
                {branding.github_url && (
                  <Menu.Item
                    label={t("nav.github")}
                    href={branding.github_url}
                    icon={<IconGithub />}
                    target="_blank"
                    rel="noreferrer"
                  />
                )}
                {branding.slack_url && (
                  <Menu.Item
                    label={t("nav.slack_community")}
                    href={branding.slack_url}
                    icon={<IconSlack />}
                    target="_blank"
                    rel="noreferrer"
                  />
                )}
                {/* Receipt check — modal, so anyone handed a payout
                    receipt can verify it from anywhere in the app. */}
                <Menu.Item
                  label={t("nav.verify_receipt", undefined, "Verify a receipt")}
                  icon={<IconCheckCircle />}
                  onClick={() => setVerifyOpen(true)}
                />

                {!isNarrow && (
                  <>
                    <Menu.Divider />
                    <Menu.Item
                      icon={<IconPin />}
                      className={sidebarClass.elem("pin").toClassName()}
                      onClick={sidebarPin}
                      active={sidebarPinned}
                    >
                      {sidebarPinned ? "Unpin menu" : "Pin menu"}
                    </Menu.Item>
                  </>
                )}
              </Menu>
            </Dropdown>
          )}

          <MenubarContext.Provider value={providerValue}>
            <div
              className={contentClass
                .elem("content")
                .mod({ withSidebar: effectivePinned && sidebarOpened })
                .toClassName()}
            >
              {children}
            </div>
          </MenubarContext.Provider>
        </div>
      </VersionProvider>
      {/* A host mode that carries its handbook in its own navigation can
          switch the floating link off; two ways to the same page, one of
          them hovering over the content, is one too many. */}
      {activeHostMode?.docsFab === false ? null : activeHostMode?.docsHref ? (
        <DocsFAB href={activeHostMode.docsHref} label={activeHostMode.docsLabel || "Handbook"} />
      ) : (
        <HotkeyFAB />
      )}
      <GlobalSyncModal />
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
      {/* The Report button steps aside whenever the sidebar is showing
          (pinned or floating), and does not appear at all on a host that
          is not the platform: the directory carries its own reporting,
          and a second floating button over its pages is chrome from a
          different product. */}
      {activeHostMode?.reportFab === false ? null : <TestReportLauncher shifted={!!sidebarOpened} />}
      {verifyOpen && (
        <Modal visible onHide={() => setVerifyOpen(false)} title="Verify a receipt" style={{ width: 480 }}>
          <div style={{ padding: "4px 8px 8px" }}>
            <VerifyReceiptForm />
          </div>
        </Modal>
      )}
    </div>
  );
};

/**
 * Mount the SyncModal once at the top of the app so any caller —
 * the menubar's "to push" pill, the connection-restored toast, the
 * Cloud sync page's "Sync now" button — can pop it open by writing
 * ``true`` to ``syncModalOpenAtom`` instead of pushing the user
 * onto a different route or wiring per-page state.
 */
const GlobalSyncModal = () => {
  const desktop = isDesktopApp();
  const [open, setOpen] = useAtom(syncModalOpenAtom);
  if (!desktop) return null;
  return (
    <SyncModal
      open={open}
      onClose={() => setOpen(false)}
      onComplete={() => {
        /* hook up a refresh poke later if needed */
      }}
    />
  );
};
